import React, { useState, useCallback } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useLazyCommand, useEvent } from '@soap/modules';
import { scheduleLogoutFromHomeHash } from '../../../constants/postLogoutReturnUrl';

interface CloseAccountDialogProps {
    onClose: () => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

const CLOSE_ACCOUNT_CONFIRMATION = 'I Understand';

function isCloseAccountConfirmationValid(text: string): boolean {
    return text.trim().toLowerCase() === CLOSE_ACCOUNT_CONFIRMATION.toLowerCase();
}

export function CloseAccountDialog({ onClose }: CloseAccountDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [confirmationText, setConfirmationText] = useState('');
    const [sendCloseAccountCommand] = useLazyCommand('C246v1_CloseAccount');

    // Listen for the AccountClosed event - when received, go to public /logout funnel then Auth0
    useEvent({
        eventName: 'E198v1_AccountClosed',
        onEventReceived: () => {
            scheduleLogoutFromHomeHash(onClose);
        }
    }, [onClose]);

    const handleClose = useCallback(() => {
        setViewState(viewStates.ready);
        setConfirmationText('');
        onClose();
    }, [onClose]);

    const handleCloseAccount = async () => {
        setViewState(viewStates.submitting);
        // After successful account closure, the user will be logged out
        // The backend will handle redirecting/logging out the user
        // Errors are handled by useApiErrors hook and global error handler
        await sendCloseAccountCommand({});
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-destructive-action flex items-center">
                            <AlertTriangle className="w-6 h-6 mr-2" />
                            Close Account
                        </h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Close dialog"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-destructive-action/10 p-4 rounded-lg border-2 border-destructive-action/40">
                            <p className="text-base font-semibold text-destructive-action-dark mb-3">
                                WARNING: This action is PERMANENT and CANNOT be undone.
                            </p>
                            <div className="space-y-2 mb-2">
                                <p className="text-sm text-destructive-action-dark">
                                    All my data will be deleted permanently.
                                </p>
                                <input
                                    type="text"
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value)}
                                    placeholder="Type 'I Understand'"
                                    className="w-full px-3 py-2 border border-destructive-action/40 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-destructive-action focus:border-destructive-action"
                                    disabled={viewState === viewStates.submitting}
                                />
                            </div>
                            <p className="text-sm text-destructive-action-dark font-medium">
                                Are you sure you want to proceed with closing your account?
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCloseAccount}
                            className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting || !isCloseAccountConfirmationValid(confirmationText)}
                        >
                            <AlertTriangle size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Closing Account...' : 'Yes, Close My Account'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
